import { Col, Row } from 'antd';
import { NotificationType } from '@app/components/common/Notification/Notification';
import { ReactComponent as ETHIcon } from '@app/assets/icons/eth.svg';
import { ReactComponent as BTCIcon } from '@app/assets/icons/btc.svg';

import visa from '@app/assets/images/card-issuers/visa.png';
import mastercard from '@app/assets/images/card-issuers/mastercard.png';
import maestro from '@app/assets/images/card-issuers/maestro.png';
import { CurrencyTypeEnum } from '@app/interfaces/interfaces';

const DateParser = require('any-date-parser');

export const camelize = (string: string): string => {
  return string
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)))
    .join('');
};

export const parseDate = (dateString: string): Date | null => {
  let date = new Date();
  try {
    date = DateParser.fromString(dateString);
    if (isNaN(date.getTime())) {
      throw new Error('unable to auto detect date using any-date-p');
    }
  } catch (e) {
    date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  }
  return date;
};

export function generateTeaser(comment: string, linesToInclude = 3, wordLimit = 20) {
  if (!comment) return { teaser: '', fullComment: '' };

  const commentByLines = comment.split('\n');
  let fullComment = comment;
  let teaser;

  if (commentByLines.length > 1) {
    // Handle comments with multiple line breaks
    teaser = commentByLines.slice(0, linesToInclude).join('\n');
    fullComment = commentByLines.slice(linesToInclude).join('\n').trim();

    if (!fullComment) {
      fullComment = teaser;
    }
  } else {
    // For cases with no or few line breaks
    const words = comment.split(' ');

    if (words.length > wordLimit) {
      teaser = words.slice(0, wordLimit).join(' ');
      fullComment = words.slice(wordLimit).join(' ').trim();
    } else {
      // If the comment is shorter than the word limit, use the whole comment
      teaser = comment;
      fullComment = '';
    }
  }

  teaser = teaser?.trim();
  fullComment = fullComment?.trim();

  // Ensure teaser is not empty
  if (!fullComment) {
    fullComment = teaser;
  }

  return { teaser, fullComment };
}

export const getCurrencyPrice = (
  price: number | string,
  currency: CurrencyTypeEnum,
  isIcon = true,
): string | React.ReactNode => {
  switch (CurrencyTypeEnum[currency]) {
    case 'EUR': {
      return isIcon ? `${price} €` : `${price} EUR`;
    }

    case 'BTC': {
      return isIcon ? (
        <Row align="middle" gutter={[8, 8]}>
          <Col style={{ display: 'flex' }}>
            <BTCIcon />
          </Col>

          <Col>{price}</Col>
        </Row>
      ) : (
        `${price} BTC`
      );
    }

    case 'ETH': {
      return isIcon ? (
        <Row align="middle" gutter={[8, 8]}>
          <Col style={{ display: 'flex' }}>
            <ETHIcon />
          </Col>

          <Col>{price}</Col>
        </Row>
      ) : (
        `${price} ETH`
      );
    }

    default: {
      return isIcon ? `$${price}` : `${price} USD`;
    }
  }
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((_el: unknown, index: number) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return `${r}, ${g}, ${b}`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : '100%';

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === 'number' ? `${prop}px` : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

export const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const defineColorByStatus = (status?: string): string => {
  switch (status) {
    case 'Open':
      return '#0F82E0';
    case 'Paused':
      return '#424242';
    case 'Paid':
      return '#1da14a';
    case 'PaymentPlan':
      return '#3cc96c';
    case 'ReminderLevel':
    case 'FirstReminder':
    case 'SecondReminder':
    case 'ThirdReminder':
      return '#4f46e8';
    case 'EncashmentLevel':
    case 'FirstDunning':
      return '#e89c46';
    case 'JudicialLevel':
      return '#a32a38';
    case 'SecondDunning':
      return '#ff6100';
    case 'ThirdReminder':
    case 'ThirdDunning':
      return '#ff1500';
    case 'FourthDunning':
      return '#bf1000';
    case 'FifthDunning':
      return '#970d00';
    case 'SixthDunning':
      return '#7e0b00';
    case 'ActionRequired':
    case 'Cancelled':
    case 'Closed':
      return '#ff5252';
    default:
      return '#3cc96c';
  }
};

export function formatExcelDate(isoString?: string): string {
  if (!isoString) {
    return '';
  }

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    console.warn(`Invalid date string provided: ${isoString}`);
    return '';
  }

  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const min = String(date.getUTCMinutes()).padStart(2, '0');
  const ss = String(date.getUTCSeconds()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
}

export const defineColorBySeverity = (severity: NotificationType | undefined, rgb = false): string => {
  const postfix = rgb ? 'rgb-color' : 'color';
  switch (severity) {
    case 'error':
    case 'warning':
    case 'success':
      return `var(--${severity}-${postfix})`;
    case 'info':
    default:
      return `var(--primary-${postfix})`;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString());
  G = parseInt(((G * (100 + percent)) / 100).toString());
  B = parseInt(((B * (100 + percent)) / 100).toString());

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max == min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return {
      h,
      s,
      l,
    };
  } else {
    throw new Error('Non valid HEX color');
  }
};

export const formatNumberWithCommas = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
export const msToH = (ms: number): number => Math.floor(ms / 3600000);

export const hToMS = (h: number): number => h * 3600000;

export const getPaymentCardTypeIcon = (type: string): string | null => {
  switch (type) {
    case 'visa':
      return visa;
    case 'mastercard':
      return mastercard;
    case 'maestro':
      return maestro;
    case 'amex':
      return 'amex';
    case 'discover':
      return 'discover';
    case 'diners':
      return 'diners';
    case 'jcb':
      return 'jcb';
    case 'unionpay':
      return 'unionpay';
    default:
      return null;
  }
};

// Animation config
export const DEFAULT_LOTTIE_CONFIG = {
  loop: true,
  autoplay: true,
  animationData: require('@app/assets/lottie/success-animation.json'),
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice', // no idea wtf this value is
  },
};

// Helper function to sanitize HTML while allowing safe tags
export const sanitizeString = (input: string) => {
  if (!input) return input;

  // First handle mentions
  const mentionRegex = /@\[([^\]]+)]\([^)]*\)/g;
  let processed = input.replace(mentionRegex, "<b class='mention'>@$1</b>");

  // Remove all attributes from HTML tags
  processed = processed.replace(/<([a-z][a-z0-9]*)[^>]*>/gi, '<$1>');

  // Define allowed tags
  const allowedTags = [
    'b',
    'i',
    'em',
    'strong',
    'div',
    'span',
    'font',
    'p',
    'br',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'code',
    'a',
  ];

  // Convert all HTML tags to lowercase for consistency
  processed = processed.replace(/<\/?([a-zA-Z0-9]+)((\s+.*?)?>)/g, (match) => match.toLowerCase());

  // Escape all HTML tags
  processed = processed.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Restore allowed tags
  allowedTags.forEach((tag) => {
    const openRegex = new RegExp(`&lt;${tag}&gt;`, 'g');
    const closeRegex = new RegExp(`&lt;/${tag}&gt;`, 'g');
    processed = processed.replace(openRegex, `<${tag}>`).replace(closeRegex, `</${tag}>`);
  });

  return processed;
};
